import { z } from 'zod';

export const billingUnitSchema = z.object({
  name: z.string().trim().min(1, '请填写客户名称').max(128),
  note: z.string().trim().max(500).default(''),
});

export const BILLING_LINK_BATCH_LIMIT = 200;

export const billingLinksUpdateSchema = z
  .object({
    changes: z
      .array(
        z.object({
          linkId: z.string().min(1).max(128),
          previousUnitId: z.string().min(1).max(128).nullable(),
          linked: z.boolean(),
        }),
      )
      .min(1)
      .max(BILLING_LINK_BATCH_LIMIT),
  })
  .refine(
    ({ changes }) => new Set(changes.map((change) => change.linkId)).size === changes.length,
    {
      message: '链接不能重复',
    },
  );

export type BillingLinkAssignmentChange = z.infer<
  typeof billingLinksUpdateSchema
>['changes'][number];

export interface BillingUnit {
  id: string;
  name: string;
  note: string;
  linkedCount: number;
}

export interface BillingRange {
  from: string;
  to: string;
  days: number;
}

export interface BillingUnitUsage extends BillingUnit {
  visits: number;
}

export interface BillingLinkUsage {
  unitId: string;
  linkId: string;
  linkName: string;
  visits: number;
  linked: boolean;
  deleted: boolean;
}

export interface BillingReport {
  range: BillingRange;
  rows: BillingUnitUsage[];
}

export const BILLING_TIMEZONE = 'Asia/Shanghai';
export const BILLING_DAY_MS = 86_400_000;

/** ISO date keys are evaluated at UTC+8 midnight with an inclusive end date. */
export function parseBillingRange(from: string, to: string): BillingRange | null {
  const datePattern = /^\d{4}-\d{2}-\d{2}$/;
  if (!datePattern.test(from) || !datePattern.test(to)) return null;
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  if (
    new Date(start).toISOString().slice(0, 10) !== from ||
    new Date(end).toISOString().slice(0, 10) !== to
  )
    return null;
  const days = (end - start) / BILLING_DAY_MS + 1;
  return days >= 1 && days <= 366 ? { from, to, days } : null;
}

export function billingToday(now = Date.now()): string {
  return new Date(now + 8 * 3_600_000).toISOString().slice(0, 10);
}

export function billingCsvCell(value: string | number): string {
  const text = String(value);
  // CSV opened in Excel must not execute unit/link names as formulas.
  const first = [...text].find((char) => char.charCodeAt(0) > 32 && char.trim() !== '');
  const safe = first && '=+@-'.includes(first) ? `'${text}` : text;
  return `"${safe.replaceAll('"', '""')}"`;
}

export function matchesBillingUnit(
  unit: Pick<BillingUnit, 'name' | 'note'>,
  search: string,
): boolean {
  const normalize = (value: string) => value.normalize('NFKC').toLowerCase();
  return normalize(`${unit.name} ${unit.note}`).includes(normalize(search.trim()));
}
